import type {
  Investment,
  InvestmentPayment,
  WorkflowAction,
  WorkflowEvent,
} from "@equidefi/database";
import { NumberFormatter } from "@equidefi/i18n";

import type { DTO } from "../types";
import { capitalizeEveryWord } from "./string";
import { offeringIsUnitType } from "./offerings";

// Types for investment fee calculation
export interface InvestmentFeeCalculationInput {
  amount: number | string;
  payments: Array<{
    fee: number | string | null;
    total_fee: number | string | null;
    payment_type?: string;
    status: string;
  }>;
  offering: {
    credit_card_fee: number | string | null;
    ach_fee: number | string | null;
  } | null;
}

export interface InvestmentFeeCalculationResult {
  app_fee: number;
  app_fee_rate: number;
  stripe_fee: number;
  net_amount: number;
}

// Calculate investment fees from investment data
export function calculateInvestmentFees(
  investment: InvestmentFeeCalculationInput,
): InvestmentFeeCalculationResult {
  const { amount, payments, offering } = investment;

  // Filter only successful payments
  const successfulPayments = payments.filter(
    (payment) => payment.status === "succeeded",
  );

  // Calculate totals from successful payments
  const appFeeTotal = successfulPayments.reduce((sum, payment) => {
    return sum + Number(payment.fee || 0);
  }, 0);

  const totalFeeSum = successfulPayments.reduce((sum, payment) => {
    return sum + Number(payment.total_fee || 0);
  }, 0);

  const stripeFeeTotal = totalFeeSum - appFeeTotal;
  const grossAmount = Number(amount || 0);
  const netAmount = grossAmount - totalFeeSum;

  // Get fee rate for display
  const paymentType = successfulPayments[0]?.payment_type || "card";
  const feeRate =
    paymentType === "us_bank_account"
      ? Number(offering?.ach_fee || 0) * 100
      : Number(offering?.credit_card_fee || 0) * 100;

  return {
    app_fee: appFeeTotal,
    app_fee_rate: feeRate,
    stripe_fee: stripeFeeTotal,
    net_amount: netAmount,
  };
}

export function investmentBcode(value?: string | null): string | null {
  if (!value) return null;
  const trimmedBcode = String(value || "").trim();
  return trimmedBcode || null;
}

// Format investment fee display
export function formatInvestmentFeeDisplay(
  fees: InvestmentFeeCalculationResult,
): string {
  return `$${fees.app_fee.toFixed(2)} (${fees.app_fee_rate.toFixed(2)}%)`;
}

export const paymentTypeDisplay = (payment: InvestmentPayment) =>
  payment.payment_type === "us_bank_account"
    ? "US Bank Account"
    : payment.payment_type
      ? capitalizeEveryWord(payment.payment_type)
      : "Manual";

export const investmentIsCompleted = (
  investment?: Pick<Investment, "status">,
) => {
  if (!investment) return false;
  return investment?.status !== "PENDING";
};

export const investmentDefaultEventHistoryTitle = (
  event: WorkflowEvent,
  status: WorkflowAction,
) => {
  return [
    event === "KYC" ? "KYC" : capitalizeEveryWord(event),
    capitalizeEveryWord(status),
  ].join(" ");
};

export const calculateMaxSubscriptionAmount = ({
  annualIncome,
  netWorth,
  investments = [],
}: {
  annualIncome: number;
  netWorth: number;
  investments?: Pick<DTO.Investment, "amount" | "status">[];
}): number => {
  const income = Number.isNaN(Number(annualIncome)) ? 0 : Number(annualIncome);

  const worth = Number.isNaN(Number(netWorth)) ? 0 : Number(netWorth);

  const highest = Math.max(income, worth) ?? 0;

  const investedAmount = investments.reduce((acc, currentInv) => {
    if (currentInv.status !== "REJECTED") {
      return currentInv.amount + acc;
    }
    return acc;
  }, 0);

  // Calculates 10% of annual income or net worth
  const maxAmount = highest / 10 - investedAmount;
  return Math.max(maxAmount, 0);
};

export const formatSecurities = ({
  quantity,
  offering,
  capitalize = false,
}: {
  quantity?: number;
  offering: Partial<DTO.Offering>;
  capitalize?: boolean;
}) => {
  const formattedQuantity = quantity && NumberFormatter.format(quantity);

  const isUnitType = offeringIsUnitType(offering);

  const formatText = (text: string) => {
    let result = text;
    if (!quantity || quantity > 1) result = `${text}s`;
    if (capitalize) result = capitalizeEveryWord(result);
    return result;
  };

  const label = isUnitType
    ? `${formatText("share")}/${formatText("unit")}`
    : `${formatText("share")}`;

  return {
    quantity: formattedQuantity,
    label,
    toString() {
      if (!formattedQuantity) return label;
      return `${formattedQuantity} ${label}`;
    },
  };
};
